import _ from "lodash"
import { get } from "lodash"
import { getIconPathString } from "../components/icons/pathString"
import { IFeature, IRecord } from "interfaces"
import { isValidTime } from "./time"
import dayjs from "config/dayjs"
import { Dayjs } from "dayjs"

// CUSTOM INTERFACE/TYPE
interface IMultiLanguageInfo {
  en?: string
  th?: string
  zh?: string
}
interface IFeatureDetail {
  id: number | string
  group?: object | string | string[] // Inferred types from the use of i18n's t function
  name: IMultiLanguageInfo | string
  category?: IMultiLanguageInfo[] | string | string[] | null
  level?: object | string // Inferred types from usage below
  locationDescription?: string | null
  zone?: object | string // Inferred types from usage below
}
type Location = null | IMultiLanguageInfo | string

// FUNCTION RETURN INTERFACE
interface IAmenityDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  location: Location
  venueLogoSimple?: string
  venueLogo?: string
}

interface IUnitDetail extends IFeatureDetail {
  location: Location
}

interface IEventDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  logo: string
  location: Location
  gallery: { [key: string]: unknown }[]
  startTime: Dayjs
  endTime: Dayjs
  startDate: Dayjs
  endDate: Dayjs
  locationId: string
  hasLocation: boolean
  websiteLink: string
  venueLogo: string
}

interface IPromotionDetail extends IFeatureDetail {
  description: IMultiLanguageInfo
  logo: string
  location: Location
  gallery: { [key: string]: unknown }[]
  startTime: Dayjs
  endTime: Dayjs
  startDate: Dayjs
  endDate: Dayjs
  locationId: string
  hasLocation: boolean
  websiteLink: string
  venueLogo: string
}

interface IKioskDetail extends IFeatureDetail {
  location: Location
}

interface IVenueDetail extends IFeatureDetail {
  logoSimple: string
  logo: string
  reference: string
}

export interface IOccupantDetail extends IFeatureDetail {
  logo: string
  featuredImage: string
  coverImage: string
  venueLogoSimple: string
  venueLogo: string
  phone: string
  hours: string
  website: string
  gallery: { [key: string]: unknown }[]
  promotions: IFeature[]
  privileges: IFeature[]
  description: IMultiLanguageInfo | string
  location: Location
  isFeatured: boolean
  categoryIconPath: string
}

interface IGeolocationDetail extends IFeatureDetail {
  venue: IFeature
}

interface ILevelDetail extends IFeatureDetail {
  ordinal: number
}

export interface ISectionDetail extends IFeatureDetail {
  coverImage?: string
  logo?: string
  venueLogo: string
}

interface ITaxonomyDetail extends IFeatureDetail {}

interface IFeaturePropsParsed {
  id: string
  avatarProps: {
    src: string
    path: string
  }
  primary: string
  secondary: string
  [key: string]: unknown
}

// FUNCTION TYPE DEFINITION
// GetFeatureLevel
type ParseLevelText = (feature: IFeature, lang?: string) => string

type GetFeatureId = (
  feature: IFeature,
  defaultValue?: string | number
) => string | number

type GetFeatureName = (
  feature: IFeature,
  defaultValue?: string
) => IMultiLanguageInfo | string

type GetFeatureDescription = (
  feature: IFeature,
  defaultValue?: string
) => IMultiLanguageInfo | string

type GetCategoryName = (feature: IFeature) => string | string[]

type GetAmenityDetail = (feature: IFeature) => IAmenityDetail

type GetUnitDetail = (feature: IFeature) => IUnitDetail

type GetEventDetail = (feature: IFeature) => IEventDetail

type GetPromotionDetail = (feature: IFeature) => IPromotionDetail

type GetKioskDetail = (feature: IFeature) => IKioskDetail

type GetVenueDetail = (venue: IFeature) => IVenueDetail

type GetOccupantDetail = (occupant: IFeature) => IOccupantDetail

type GetSectionDetail = (section: IFeature) => ISectionDetail

type GetGeolocationDetail = (geolocation: IFeature) => IGeolocationDetail

type GetLevelDetail = (level: IFeature) => ILevelDetail

type GetTaxonomyDetail = (taxonomy: IFeature) => ITaxonomyDetail

type GetLocationNameFromFeature = (feature: IFeature) => Location

type GetFeatureDetail = (
  feature: IFeature
) =>
  | IAmenityDetail
  | IEventDetail
  | IUnitDetail
  | IKioskDetail
  | IOccupantDetail
  | IPromotionDetail
  | ILevelDetail
  | ITaxonomyDetail
  | IVenueDetail
  | IGeolocationDetail
  | null

type ListItemPropsParserFactoryParams = ({
  t,
  hideLevel,
  hideVenueLogo,
}: {
  t: any
  hideLevel: boolean
  hideVenueLogo: boolean
}) => ListItemPropsParserFactory
type ListItemPropsParserFactory = (feature: IFeature) => IFeaturePropsParsed

type SponsoredContentListItemPropsParserFactoryParams = ({
  t,
  hideLevel,
  hideVenueLogo,
}: {
  t: any
  hideLevel: boolean
  hideVenueLogo: boolean
}) => SponsoredContentListItemPropsParserFactory
type SponsoredContentListItemPropsParserFactory = (ad: IRecord) => IRecord

export const parseLevelText: ParseLevelText = (feature, lang = "en") => {
  if (!feature?.properties?.level && !feature?.properties?.level_name) return ""

  const { level, level_name } = feature.properties
  const levelName = level?.properties?.name.en || level_name

  return levelName
}

export const getFeatureId: GetFeatureId = (feature, defaultValue = "") =>
  get(feature, "id", defaultValue)

export const getFeatureName: GetFeatureName = (feature, defaultValue = "") => {
  return get(feature, "properties.name", defaultValue)
}

export const getFeatureDescription: GetFeatureDescription = (
  feature,
  defaultValue = ""
) => get(feature, "properties.description", defaultValue)

export const getCategoryName: GetCategoryName = (feature) => {
  const { properties, feature_type } = feature
  switch (feature_type) {
    case "amenity": {
      return `${feature_type}.${properties.category}`
    }
    case "occupant": {
      const localParentCategory = get(
        feature,
        "properties.local_parent_categories"
      )
      return localParentCategory.map((category: IFeature) =>
        get(category, "properties.name", "")
      )
    }
    default:
      return ""
  }
}

const getAmenityDetail: GetAmenityDetail = (feature) => {
  const { feature_type } = feature
  const { units, category, venue, description } = feature?.properties || {}
  const categoryName = `${feature_type}.${category}`
  const levelName = get(units, "[0].properties.level.properties.name.en")
  const nameObject = getFeatureName(feature)
  const nameText = _.isEmpty(nameObject) ? categoryName : nameObject
  const zoneName = get(units, "[0].properties.section.properties.name", "")
  let locationDescription = get(feature, "properties.location_description", "")
  locationDescription = !_.isEmpty(locationDescription)
    ? locationDescription
    : null

  const locationName = getLocationNameFromFeature(feature)

  const venueProperties = get(venue, "properties")
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url

  return {
    id: getFeatureId(feature),
    name: nameText,
    level: levelName,
    zone: zoneName,
    category: categoryName,
    location: locationName,
    description,
    locationDescription: locationDescription,
    venueLogoSimple,
    venueLogo,
  }
}

const getUnitDetail: GetUnitDetail = (feature) => {
  const { name, level, section } = feature?.properties || {}
  const levelName = getFeatureName(level)
  const zoneName = getFeatureName(section)
  const locationName = getLocationNameFromFeature(feature)

  return {
    name,
    level: levelName,
    zone: zoneName,
    id: getFeatureId(feature),
    location: locationName,
  }
}
export const getEventDetail: GetEventDetail = (feature) => {
  const {
    name,
    description,
    cover_image: coverImage,
    gallery,
    website_link: websiteLink,
    feature_id: locationId,
    feature: locationFeature,
    start_date,
    end_date,
    daily_start_time,
    daily_end_time,
    venue,
  } = feature?.properties || {}
  const venueProperties = get(venue, "properties")
  const locationName = getLocationNameFromFeature(feature)
  const coverImageUrl = coverImage?.url
  const levelName = parseLevelText(feature.properties.feature)
  const startTime = isValidTime(daily_start_time)
    ? daily_start_time.slice(0, 5)
    : null
  const endTime = isValidTime(daily_end_time)
    ? daily_end_time.slice(0, 5)
    : null
  const startDate = dayjs(start_date)
  const endDate = dayjs(end_date)
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url

  // cover_image
  return {
    name,
    description,
    logo: coverImageUrl,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    level: levelName,
    websiteLink,
    locationId,
    hasLocation: !!locationFeature,
    startDate,
    endDate,
    startTime,
    endTime,
    venueLogo,
  }
}

export const getPromotionDetail: GetPromotionDetail = (feature) => {
  const {
    name,
    description,
    cover_image: coverImage,
    gallery,
    website_link: websiteLink,
    feature_id: locationId,
    feature: locationFeature,
    start_date,
    end_date,
    daily_start_time,
    daily_end_time,
    venue,
  } = feature?.properties || {}
  const venueProperties = get(venue, "properties")

  const locationName = getLocationNameFromFeature(feature)
  const coverImageUrl = coverImage?.url
  const levelName = parseLevelText(feature.properties.feature)
  const startTime = isValidTime(daily_start_time)
    ? daily_start_time.slice(0, 5)
    : null
  const endTime = isValidTime(daily_end_time)
    ? daily_end_time.slice(0, 5)
    : null
  const startDate = dayjs(start_date)
  const endDate = dayjs(end_date)
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url

  return {
    name,
    description,
    logo: coverImageUrl,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    level: levelName,
    websiteLink,
    locationId,
    hasLocation: !!locationFeature,
    startDate,
    endDate,
    startTime,
    endTime,
    venueLogo,
  }
}

const getKioskDetail: GetKioskDetail = (feature) => {
  const { name, level, section } = feature?.properties || {}
  const zoneName = get(section, "properties.name") || ""
  const levelName = getFeatureName(level)
  const locationName = getLocationNameFromFeature(feature)

  return {
    name,
    zone: zoneName,
    level: levelName,
    id: getFeatureId(feature),
    location: locationName,
  }
}

export const getVenueDetail: GetVenueDetail = (venue) => {
  const {
    name,
    logo: logoField,
    logo_simple: logoSimpleField,
    reference,
  } = venue?.properties || {}

  const logoSimple =
    logoSimpleField?.formats?.thumbnail?.url || logoSimpleField?.url
  const logo = logoField?.formats?.thumbnail?.url || logoField?.url

  return {
    id: getFeatureId(venue),
    name,
    logoSimple,
    logo,
    reference,
  }
}

export const getOccupantDetail: GetOccupantDetail = (occupant) => {
  const { properties } = occupant
  const {
    name,
    local_parent_categories,
    logo,
    featured_image,
    cover_image,
    level_name,
    anchor,
    hours,
    phone,
    groups,
    venue,
    is_featured,
  } = occupant?.properties || {}
  const venueProperties = get(venue, "properties")

  const categoryIconPath = local_parent_categories?.[0]?.properties?.icon_path
  const categoryName = local_parent_categories?.[0]?.properties.name
  const groupName = groups?.[0]?.properties.name
  const zoneName = get(anchor, "properties.section.properties.name") || ""
  const logoImage = logo?.formats?.thumbnail?.url || logo?.url
  const featuredImage =
    featured_image?.formats?.thumbnail?.url || featured_image?.url
  const coverImage = cover_image?.formats?.large?.url || cover_image?.url
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const venueName = getFeatureName(venue)
  const website = get(properties, "website")
  const gallery = get(properties, "gallery")
  const promotions = get(properties, "promotions")
  const privileges = get(properties, "privileges")
  const description = getFeatureDescription(occupant)
  const locationName = getLocationNameFromFeature(occupant)
  const isFeatured = is_featured

  return {
    id: getFeatureId(occupant),
    zone: zoneName,
    logo: logoImage,
    featuredImage,
    coverImage,
    category: categoryName,
    categoryIconPath,
    venueLogoSimple,
    venueLogo,
    venueName,
    phone,
    level: level_name,
    name,
    hours,
    website,
    gallery,
    promotions,
    privileges,
    group: groupName,
    description: description,
    location: locationName,
    isFeatured,
  }
}
const getGeolocationDetail: GetGeolocationDetail = (geolocation) => {
  const { name, level_name, venue } = geolocation?.properties || {}
  return {
    id: getFeatureId(geolocation),
    name,
    level: level_name,
    venue,
  }
}

export const getSectionDetail: GetSectionDetail = (section) => {
  const { properties } = section
  const {
    name,
    cover_image,
    logo,
    hours,
    venue,
    local_parent_categories,
    level_name,
  } = properties || {}

  const coverImage = cover_image?.url
  const logoImage = logo?.formats?.thumbnail?.url || logo?.url
  const venueName = getFeatureName(venue)

  const categoryIconPath = local_parent_categories?.[0]?.properties?.icon_path
  const categoryName = local_parent_categories?.[0]?.properties.name

  const venueProperties = venue?.properties
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const description = getFeatureDescription(section)

  return {
    id: getFeatureId(section),
    name,
    description,
    level: level_name,
    coverImage,
    featuredImage: coverImage,
    logo: logoImage,
    hours,
    venueName,
    category: categoryName,
    categoryIconPath,
    venueLogo,
  }
}

const getLevelDetail: GetLevelDetail = (level) => {
  const { properties } = level
  const { name, ordinal } = properties || {}
  return {
    id: getFeatureId(level),
    name,
    ordinal,
  }
}

export const getTaxonomyDetail: GetTaxonomyDetail = (taxonomy) => {
  const { properties } = taxonomy
  const { name } = properties || {}
  return { id: getFeatureId(taxonomy), name }
}

export const getLocationNameFromFeature: GetLocationNameFromFeature = (
  feature
) => {
  const { feature_type } = feature || {}
  const properties = get(feature, "properties")

  switch (feature_type) {
    case "unit":
      return getFeatureName(get(properties, "section"))
    case "kiosk":
      return getFeatureName(
        get(properties, "anchor.properties.unit.properties.section")
      )
    case "amenity":
      return getFeatureName(get(properties, "units[0]"))
    case "occupant":
      const kiosk = get(properties, "kiosk")
      const unit = get(properties, "anchor.properties.unit")
      return getFeatureName(kiosk) || getFeatureName(unit)
    case "event":
    case "promotion":
    case "level":
      const feature = get(properties, "feature")
      return getFeatureName(feature)
    default:
      return null
  }
}

export const getFeatureDetail: GetFeatureDetail = (feature) => {
  const { feature_type } = feature || {}
  switch (feature_type) {
    case "amenity":
      return getAmenityDetail(feature)
    case "unit":
      return getUnitDetail(feature)
    case "kiosk":
      return getKioskDetail(feature)
    case "occupant":
      return getOccupantDetail(feature)
    case "event":
      return getEventDetail(feature)
    case "promotion":
      return getPromotionDetail(feature)
    case "level":
      return getLevelDetail(feature)
    case "taxonomy":
      return getTaxonomyDetail(feature)
    case "venue":
      return getVenueDetail(feature)
    case "geolocation":
      return getGeolocationDetail(feature)
    case "section":
      return getSectionDetail(feature)
    default:
      return null
  }
}

/** Create ListItem Props  */
export const listItemPropsParserFactory: ListItemPropsParserFactoryParams =
  ({ t, hideLevel, hideVenueLogo }): ListItemPropsParserFactory =>
  (feature) => {
    const { feature_type, id, properties } = feature
    switch (feature_type) {
      case "amenity": {
        const {
          group,
          name,
          /* category,  */ level,
          locationDescription,
          zone,
        } = getFeatureDetail(feature)
        /* const categoryText = t(category) */
        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`

        const locationDescriptionText =
          locationDescription && t(locationDescription)
        const primary =
          // The name can be either an IMDF category name ("restroom.female") or a custom name ("Toilet A").
          t(name)
        const groupText = group && t(group)
        const secondary = locationDescriptionText
          ? _.compact([floorText, locationDescriptionText]).join(" - ") // Floor GF - Next to McDonald
          : _.compact([
              _.compact([floorText, groupText]).join(" - "),
              zoneText,
            ]).join(" | ")
        const avatarSrc =
          properties?.logo?.formats?.thumbnail?.url || properties?.logo?.url
        const categoryIconPath = getIconPathString(properties.category)
        const venueLogo =
          !hideVenueLogo && properties?.venue?.properties?.logo?.url

        return {
          id,
          avatarProps: {
            src: avatarSrc,
            path: categoryIconPath,
          },
          venueLogo,
          primary,
          secondary,
          ...feature,
        }
      }
      case "occupant":
      default: {
        const { group, name, category, level, zone, categoryIconPath } =
          getFeatureDetail(feature) as IOccupantDetail
        const categoryText = category && t(category)

        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`

        const primary = t(name)
        const groupText = group && t(group)
        const secondary = _.compact([
          _.compact([categoryText, floorText, groupText]).join(" - "),
          zoneText,
        ]).join(" | ")

        const avatarSrc =
          properties?.logo?.formats?.thumbnail?.url || properties?.logo?.url
        const venueLogo =
          !hideVenueLogo && properties?.venue?.properties?.logo?.url

        return {
          id,
          avatarProps: {
            src: avatarSrc,
            path: categoryIconPath,
          },
          venueLogo,
          primary,
          secondary,
          ...feature,
        }
      }
    }
  }

/** Create sponsoredContentListItem Props  */
export const sponsoredContentListItemPropsParserFactory: SponsoredContentListItemPropsParserFactoryParams =

    ({
      t,
      hideLevel,
      hideVenueLogo,
    }): SponsoredContentListItemPropsParserFactory =>
    (ad) => {
      const { feature, feature_id } = ad
      const { level, zone, category, categoryIconPath, group, name, logo } =
        feature
      const floorText = !hideLevel && `${t("Level Text", { level: t(level) })}`
      const zoneText = zone && `${t("Zone")} ${t(zone)}`
      const categoryText = category && t(category)
      const groupText = group && t(group)
      const primary = t(name)

      const secondary = _.compact([
        _.compact([categoryText, floorText, groupText]).join(" - "),
        zoneText,
      ]).join(" | ")
      const avatarSrc = logo
      const venueLogo = !hideVenueLogo && feature.venueLogo
      return {
        id: feature_id,
        avatarProps: {
          src: avatarSrc,
          path: categoryIconPath,
        },
        venueLogo,
        primary,
        secondary,
        isAd: true,
      }
    }
